import { RotateCcw, SlidersHorizontal, X } from 'lucide-react';
import type { Meta } from '@upbeat/shared';
import type { FilterState } from '../state/filters';
import { Chips } from './Chips';
import { RangeSlider, SingleYearSlider } from './RangeSlider';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Button } from './ui/button';
import { Separator } from './ui/separator';
import { t } from '../i18n';
import { useLocation } from 'react-router-dom';
import { AFRICAN_COUNTRY_LIST, resolveCountryIso3 } from '@upbeat/shared';
import { DEFAULT_COMPARE_COLORS } from '../charts/palette';

export type RouteFilterConfig = {
  showIndicator: boolean;
  showCountry: boolean;
  yearMode: 'range' | 'single' | 'none';
  showRegions: boolean;
  showIncome: boolean;
  showOwnership: boolean;
};

export const DEFAULT_FILTER_CONFIG: RouteFilterConfig = {
  showIndicator: true,
  showCountry: false,
  yearMode: 'range',
  showRegions: true,
  showIncome: true,
  showOwnership: true,
};

/** Declarative, maintainable filter configuration per route */
export const ROUTE_FILTER_CONFIG: Record<string, RouteFilterConfig> = {
  '/': DEFAULT_FILTER_CONFIG,
  '/trends': DEFAULT_FILTER_CONFIG,
  '/benchmarking': DEFAULT_FILTER_CONFIG,
  '/map': {
    showIndicator: true,
    showCountry: true,
    yearMode: 'single',
    showRegions: false, // Map is specifically for African utilities
    showIncome: true,
    showOwnership: true,
  },
};

interface Props {
  meta: Meta | undefined;
  state: FilterState | null;
  open: boolean;
  onChange: (patch: Partial<FilterState>) => void;
  onReset: () => void;
  country?: string | null;
  onCountryChange?: (country: string | null) => void;
  selectedCountries?: string[];
  onSelectedCountriesChange?: (countries: string[] | ((prev: string[]) => string[])) => void;
}

import { AFR_MAX_YEAR } from '../state/filters';

export function isFilterStateDefault(
  state: FilterState,
  meta: Meta,
  config: RouteFilterConfig,
  country?: string | null,
  maxMapYear?: number
): boolean {
  const effectiveMaxMapYear = maxMapYear ?? Math.min(meta.years.to, AFR_MAX_YEAR);

  if (config.showIndicator && state.indicator !== meta.default_indicator) {
    return false;
  }
  if (config.showCountry && Boolean(country)) {
    return false;
  }
  if (config.showRegions && state.region.length !== meta.regions.length) {
    return false;
  }
  if (config.showIncome && state.income.length !== meta.income_groups.length) {
    return false;
  }
  if (config.showOwnership && state.ownership.length !== meta.ownerships.length) {
    return false;
  }
  if (config.yearMode === 'range') {
    if (state.from !== meta.years.from || state.to !== meta.years.to) {
      return false;
    }
  } else if (config.yearMode === 'single') {
    const currentMapYear = state.mapYear ?? effectiveMaxMapYear;
    if (currentMapYear !== effectiveMaxMapYear) {
      return false;
    }
  }
  return true;
}

export function hasActiveFilters(
  state: FilterState | null | undefined,
  meta: Meta | undefined,
  config: RouteFilterConfig,
  country?: string | null,
  maxMapYear?: number
): boolean {
  if (!state || !meta) return false;
  return !isFilterStateDefault(state, meta, config, country, maxMapYear);
}

export function Filters({
  meta,
  state,
  open,
  onChange,
  onReset,
  country,
  onCountryChange,
  selectedCountries,
  onSelectedCountriesChange,
}: Props) {
  const location = useLocation();
  const config = ROUTE_FILTER_CONFIG[location.pathname] ?? DEFAULT_FILTER_CONFIG;
  const maxMapYear = meta ? Math.min(meta.years.to, AFR_MAX_YEAR) : AFR_MAX_YEAR;
  const hasAppliedFilters = hasActiveFilters(state, meta, config, country, maxMapYear);

  return (
    <aside
      id="filters"
      className={open ? 'flex flex-col gap-6 w-full' : 'sidebar flex flex-col gap-6'}
      aria-label={t.filters}
    >
      {/* Header without horizontal divider */}
      <div className="flex items-center justify-between pb-1 min-h-[28px]">
        <div className="flex items-center gap-2">
          <SlidersHorizontal className="h-4 w-4 text-gold" />
          <span className="text-xs font-bold uppercase tracking-[2px] text-gold2">
            {t.filters}
          </span>
        </div>
        {hasAppliedFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onReset}
            className="h-7 px-2 text-dim hover:text-gold2 flex items-center gap-1.5 text-xs font-semibold"
            title={t.reset}
          >
            <RotateCcw className="h-3 w-3" />
            {t.reset}
          </Button>
        )}
      </div>

      {!meta || !state ? (
        <div className="space-y-4 animate-pulse">
          <div className="h-10 rounded-card bg-white/[0.04]" />
          <div className="h-16 rounded-card bg-hair/40 animate-pulse" />
          <div className="h-24 rounded-card bg-hair/40 animate-pulse" />
          <div className="h-20 rounded-card bg-hair/40 animate-pulse" />
        </div>
      ) : (
        <div className="space-y-5">
          {/* Indicator Select (always shown) */}
          {config.showIndicator && (
            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-txt/90">
                {t.indicator}
              </label>
              <Select
                value={state.indicator}
                onValueChange={(indicator) => onChange({ indicator })}
              >
                <SelectTrigger className="w-full text-left font-medium">
                  <SelectValue placeholder={t.indicator} />
                </SelectTrigger>
                <SelectContent className="max-h-80">
                  {meta.indicators.map((g) => (
                    <SelectGroup key={g.component}>
                      <SelectLabel>{g.component}</SelectLabel>
                      {g.indicators.map((i) => (
                        <SelectItem key={i.indicator} value={i.indicator}>
                          {i.indicator}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  ))}
                </SelectContent>
              </Select>
              {(() => {
                const activeMeta = meta.indicators
                  .flatMap((g) => g.indicators)
                  .find((i) => i.indicator === state.indicator);
                if (!activeMeta?.description) return null;
                return (
                  <p className="text-[10.5px] text-muted-foreground leading-snug pt-0.5">
                    {activeMeta.description}
                  </p>
                );
              })()}
            </div>
          )}

          {/* Country Multi-Select (shown on /map) */}
          {config.showCountry && (
            <>
              <Separator className="bg-hair/50" />
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-semibold uppercase tracking-wider text-txt/90">
                    {t.country}
                  </label>
                  {selectedCountries && selectedCountries.length > 0 && (
                    <span className="text-[10px] text-dim font-mono">
                      {selectedCountries.length} / 4
                    </span>
                  )}
                </div>

                {/* Selected Country Badges */}
                {selectedCountries && selectedCountries.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 pb-0.5">
                    {selectedCountries.map((c, i) => {
                      const color = DEFAULT_COMPARE_COLORS[i % DEFAULT_COMPARE_COLORS.length];
                      const iso = resolveCountryIso3(c);
                      return (
                        <span
                          key={c}
                          className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium border bg-glass-2"
                          style={{ borderColor: `${color}66` }}
                        >
                          <span
                            className="w-2 h-2 rounded-full shrink-0"
                            style={{ backgroundColor: color }}
                          />
                          <span className="text-txt text-[11px]">{c}</span>
                          {iso && (
                            <span className="font-mono text-[9px] text-dim uppercase">
                              ({iso})
                            </span>
                          )}
                          <button
                            type="button"
                            onClick={() => {
                              const updated = selectedCountries.filter((x) => x !== c);
                              onSelectedCountriesChange?.(updated);
                            }}
                            className="text-dim hover:text-txt rounded-full p-0.5 ml-0.5"
                            title={t.removeCountry(c)}
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      );
                    })}
                  </div>
                )}

                {/* Country dropdown selector to add a country */}
                {(!selectedCountries || selectedCountries.length < 4) && (
                  <Select
                    key={`country-add-${selectedCountries?.join('-') || 'empty'}`}
                    onValueChange={(val) => {
                      if (!val) return;
                      const current = selectedCountries ?? (country ? [country] : []);
                      if (!current.includes(val) && current.length < 4) {
                        const updated = [...current, val];
                        onSelectedCountriesChange?.(updated);
                      }
                    }}
                  >
                    <SelectTrigger className="w-full text-left font-medium h-8 text-xs">
                      <SelectValue placeholder={selectedCountries?.length ? "+ Add country" : t.country} />
                    </SelectTrigger>
                    <SelectContent className="max-h-80">
                      {AFRICAN_COUNTRY_LIST.map((name) => {
                        const isSelected = selectedCountries?.includes(name) || country === name;
                        const iso = resolveCountryIso3(name);
                        return (
                          <SelectItem
                            key={name}
                            value={name}
                            disabled={isSelected}
                            className="text-xs"
                          >
                            <span>{name}</span>
                            {iso && (
                              <span className="font-mono text-[10px] text-dim uppercase ml-1.5">
                                ({iso})
                              </span>
                            )}
                            {isSelected ? ' ✓' : ''}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </>
          )}

          {/* Year Selector: Single year for /map, RangeSlider for other routes */}
          {config.yearMode === 'range' && (
            <>
              <Separator className="bg-hair/50" />
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-txt/90">
                  {t.yearRange}
                </label>
                <RangeSlider
                  min={meta.years.from}
                  max={meta.years.to}
                  from={state.from}
                  to={state.to}
                  onChange={(from, to) => onChange({ from, to })}
                />
              </div>
            </>
          )}

          {config.yearMode === 'single' && (
            <>
              <Separator className="bg-hair/50" />
              <SingleYearSlider
                min={meta.years.from}
                max={maxMapYear}
                value={state.mapYear ?? maxMapYear}
                onChange={(y) => onChange({ mapYear: y })}
              />
            </>
          )}

          {/* Region Chips (shown on global routes, hidden on /map) */}
          {config.showRegions && (
            <>
              <Separator className="bg-hair/50" />
              <Chips
                label={t.region}
                options={meta.regions.map((r) => ({ value: r.code, label: r.label }))}
                selected={state.region}
                onChange={(region) => onChange({ region })}
              />
            </>
          )}

          {/* Income Group Chips */}
          {config.showIncome && (
            <>
              <Separator className="bg-hair/50" />
              <Chips
                label={t.incomeGroup}
                options={meta.income_groups.map((g) => ({ value: g, label: g }))}
                selected={state.income}
                onChange={(income) => onChange({ income })}
              />
            </>
          )}

          {/* Ownership Chips */}
          {config.showOwnership && (
            <>
              <Separator className="bg-hair/50" />
              <Chips
                label={t.ownership}
                options={meta.ownerships.map((o) => ({ value: o, label: o }))}
                selected={state.ownership}
                onChange={(ownership) => onChange({ ownership })}
              />
            </>
          )}
        </div>
      )}

      {/* Note without horizontal divider */}
      <p className="mt-auto pt-4 text-xs text-dim leading-relaxed">
        {t.mockNote}
      </p>
    </aside>
  );
}
